import { useParams } from "react-router";
import t from "@/lang/lang";
import { useQpmOfferShowQuery } from "@/services/backend/qpm/offers/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Separator } from "@/shared/components/ui/separator";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { OfferCertifiedTrainingCoursePriceEditor } from "@/routes/processes/[processId]/qpm/offers/[offerId]/overview/_components/offer-certified-training-course-price-editor";
import { OfferContractTermsEditor } from "@/routes/processes/[processId]/qpm/offers/[offerId]/overview/_components/offer-contract-terms-editor";
import { OfferIoTRentalServiceTextEditor } from "@/routes/processes/[processId]/qpm/offers/[offerId]/contract/_components/offer-iot-rental-service-text-editor";
import { OfferIoTTargetOrganismEditor } from "@/routes/processes/[processId]/qpm/offers/[offerId]/contract/_components/offer-iot-target-organism-editor";
import {
  hasVisibleRentingSection,
  resolvedPriceAvailability,
  resolvedPriceVisibility,
} from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility";

export function OfferContractRoute() {
  const params = useParams();
  const { offerId } = params;
  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId! }, { skip: !offerId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="space-y-3 px-4 py-4 sm:px-6">
          <Skeleton className="h-7 w-64" />
          <Separator />
        </CardHeader>
        <CardContent className="space-y-4 px-4 pb-6 sm:px-6">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-44 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!offer) {
    return null;
  }

  const rentingAvailability = resolvedPriceAvailability(offer).renting.aLoRa;
  const rentingVisibleInPdf = resolvedPriceVisibility(offer).renting.aLoRa;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/20 space-y-3 px-4 py-4 sm:px-6">
        <CardTitle className="text-lg font-semibold sm:text-xl">
          {t("Vertrag")}
        </CardTitle>
        <Separator />
      </CardHeader>
      <CardContent className="space-y-6 px-4 py-5 sm:px-6">
        <section className="space-y-4 rounded-lg border p-4 sm:p-5">
          <h3 className="text-base leading-tight font-semibold">
            {t("Vertragsdaten")}
          </h3>
          <div className="grid gap-4 lg:grid-cols-2">
            <OfferCertifiedTrainingCoursePriceEditor offer={offer} />
          </div>
          <Separator />
          <OfferContractTermsEditor offer={offer} />
        </section>

        {hasVisibleRentingSection(offer) || rentingAvailability ? (
          <section className="space-y-4 rounded-lg border p-4 sm:p-5">
            <h3 className="text-base leading-tight font-semibold">
              {t("IoT Mietvertrag")}
            </h3>
            <OfferIoTTargetOrganismEditor
              offer={offer}
              disabledByPriceVisibility={!rentingVisibleInPdf}
            />
            <Separator />
            <OfferIoTRentalServiceTextEditor
              offer={offer}
              disabledByPriceVisibility={!rentingVisibleInPdf}
            />
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}
